import {
  fork,
  put,
  all,
  call,
  takeLeading,
  takeLatest,
  take,
  cancel,
} from 'redux-saga/effects';
import types from './actionTypes';
import * as countriesActions from './actions';
import {
  getFirestore,
  collection,
  doc,
  query,
  orderBy,
  serverTimestamp,
  deleteField,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import toastr from 'toastr';
import { toDateFirebase } from '../../helpers/sharedFunction';

const db = getFirestore(rsf.app);

export function* createCountrySaga({ country }) {
  try {
    const countriesRef = collection(db, 'countries');
    // callCenter is null when the form has no open weekday: a new country
    // simply goes without it.
    const { callCenter, ...countryData } = country;

    yield call(rsf.firestore.addDocument, countriesRef, {
      ...countryData,
      ...(callCenter && { callCenter }),
      createdAt: serverTimestamp(),
    });
    yield put(countriesActions.createCountrySuccess(country));
    toastr.success('Country created!', '');
  } catch (error) {
    yield put(countriesActions.createCountryFailure(error));
  }
}

export function* updateCountrySaga({ country }) {
  try {
    const countriesRef = doc(db, 'countries', country.id);
    delete country.id;

    yield call(
      rsf.firestore.setDocument,
      countriesRef,
      {
        ...country,
        // null from the form means no open weekday, and the field has to go:
        // the save is a merge, which would keep the old hours, and the cloud
        // functions read any callCenter.workingTime as a configured call
        // center — a week of closed days would mean "always closed".
        ...(country.callCenter === null && { callCenter: deleteField() }),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    yield put(countriesActions.updateCountrySuccess(country));
    toastr.success('Country updated!', '');
  } catch (error) {
    yield put(countriesActions.updateCountryFailure(error));
  }
}

const countryTransformer = (payload) => {
  let countries = [];

  payload.forEach((country) => {
    const data = country.data();
    countries.push({
      id: country.id,
      ...data,
      ...(data.facebookTokenExpiration && {
        facebookTokenExpiration: toDateFirebase(
          country,
          data,
          'facebookTokenExpiration',
        ).toDate(),
      }),
      ...(data.facebookTokenUpdatedAt && {
        facebookTokenUpdatedAt: toDateFirebase(
          country,
          data,
          'facebookTokenUpdatedAt',
        ).toDate(),
      }),
      createdAt: toDateFirebase(country, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(country, data).toDate(),
      }),
    });
  });

  return countries;
};

function* syncCountriesSaga() {
  const countriesRef = query(
    collection(db, 'countries'),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, countriesRef, {
    successActionCreator: countriesActions.syncCountriesSuccess,
    failureActionCreator: countriesActions.syncCountriesFailure,
    transform: (payload) => countryTransformer(payload),
  });

  yield take(types.RESET_COUNTRY_STATE);
  yield cancel(task);
}

function* countrySaga() {
  yield all([
    takeLatest(types.SYNC_COUNTRIES.REQUEST, syncCountriesSaga),
    takeLeading(types.CREATE_COUNTRY.REQUEST, createCountrySaga),
    takeLeading(types.UPDATE_COUNTRY.REQUEST, updateCountrySaga),
  ]);
}

export default countrySaga;
